/**
 * Device push token registration for business/customer alert delivery.
 */
import { GHALISHOR_API_V1_PREFIX } from "./identity-access";
import type { IsoDateTimeString, UuidString } from "./identity-access";

export const PUSH_PLATFORMS = Object.freeze(["android", "ios", "web"] as const);
export type PushPlatform = (typeof PUSH_PLATFORMS)[number];

export const PUSH_CLIENTS = Object.freeze([
  "customer",
  "business",
  "platform",
] as const);
export type PushClientKind = (typeof PUSH_CLIENTS)[number];

export const PUSH_CAMPAIGN_AUDIENCES = Object.freeze(["customers"] as const);
export type PushCampaignAudience = (typeof PUSH_CAMPAIGN_AUDIENCES)[number];

export const PUSH_ROUTES = Object.freeze({
  registerDevice: `${GHALISHOR_API_V1_PREFIX}/devices/push-token`,
  publicFirebaseConfig: `${GHALISHOR_API_V1_PREFIX}/public/firebase-config`,
  platformCampaigns: `${GHALISHOR_API_V1_PREFIX}/platform/push/campaigns`,
  platformBusinessMessages: `${GHALISHOR_API_V1_PREFIX}/platform/notifications/business-messages`,
  platformCustomerReminders: `${GHALISHOR_API_V1_PREFIX}/platform/notifications/customer-reminders`,
  platformNotificationHistory: `${GHALISHOR_API_V1_PREFIX}/platform/notifications/history`,
  cancelPlatformNotification: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/platform/notifications/${encodeURIComponent(id)}/cancel`,
  businessMessages: `${GHALISHOR_API_V1_PREFIX}/business/notifications`,
  readBusinessMessage: (id: string) =>
    `${GHALISHOR_API_V1_PREFIX}/business/notifications/${encodeURIComponent(id)}/read`,
} as const);

export interface RegisterPushTokenRequest {
  readonly token: string;
  readonly platform: PushPlatform;
  readonly clientKind: PushClientKind;
  readonly businessId?: UuidString | null;
}

export interface RegisterPushTokenResponse {
  readonly success: true;
  readonly updatedAt: IsoDateTimeString;
}

export interface PublicFirebaseConfigResponse {
  readonly enabled: boolean;
  readonly apiKey: string;
  readonly appId: string;
  readonly messagingSenderId: string;
  readonly projectId: string;
  readonly authDomain: string;
  readonly storageBucket: string;
  readonly vapidKey: string;
}

export interface SendPushCampaignRequest {
  readonly audience: PushCampaignAudience;
  readonly title: string;
  readonly body: string;
  readonly url?: string | null;
}

export interface SendPushCampaignResponse {
  readonly id: UuidString;
  readonly recipientCount: number;
  readonly sentCount: number;
  readonly deliveryConfigured: boolean;
  readonly createdAt: IsoDateTimeString;
}

export type PlatformNotificationKind =
  | "business_direct"
  | "customer_reminder"
  | "support_alert";
export type PlatformNotificationStatus =
  "scheduled" | "processing" | "sent" | "cancelled" | "failed";

export interface SendBusinessDirectMessageRequest {
  readonly businessId: UuidString;
  readonly title: string;
  readonly body: string;
  readonly url?: string | null;
}

export interface ScheduleCustomerReminderRequest {
  readonly title: string;
  readonly body: string;
  readonly scheduledFor: IsoDateTimeString;
  readonly url?: string | null;
}

export interface PlatformNotificationSummary {
  readonly id: UuidString;
  readonly kind: PlatformNotificationKind;
  readonly status: PlatformNotificationStatus;
  readonly businessId: UuidString | null;
  readonly businessName: string | null;
  readonly title: string;
  readonly body: string;
  readonly targetUrl: string | null;
  readonly scheduledFor: IsoDateTimeString;
  readonly recipientCount: number;
  readonly sentCount: number;
  readonly readCount: number;
  readonly deliveryConfigured: boolean | null;
  readonly lastError: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly sentAt: IsoDateTimeString | null;
}

export interface ListPlatformNotificationsResponse {
  readonly items: readonly PlatformNotificationSummary[];
}

export interface BusinessDirectMessageSummary {
  readonly id: UuidString;
  readonly title: string;
  readonly body: string;
  readonly targetUrl: string | null;
  readonly createdAt: IsoDateTimeString;
  readonly readAt: IsoDateTimeString | null;
  readonly senderName: string;
}

export interface ListBusinessDirectMessagesResponse {
  readonly items: readonly BusinessDirectMessageSummary[];
  readonly unreadCount: number;
}

export function isPushPlatform(value: unknown): value is PushPlatform {
  return (
    typeof value === "string" &&
    (PUSH_PLATFORMS as readonly string[]).includes(value)
  );
}

export function isPushClientKind(value: unknown): value is PushClientKind {
  return (
    typeof value === "string" &&
    (PUSH_CLIENTS as readonly string[]).includes(value)
  );
}
